import { Button } from "@/components/ui/button";
import { fmt, RepairEvent, RepairChoice } from "@/hooks/useVehicleSim";
import { AlertTriangle, CreditCard, PiggyBank, Clock } from "lucide-react";

interface Props {
  repair: RepairEvent;
  vehicleName: string;
  emergencyFund: number;
  onChoose: (choice: RepairChoice) => void;
}

/**
 * Mid-ownership shock event. Surfaces a deterministic repair scaled to
 * the chosen vehicle's reliability so the math the learner just made
 * is now stress-tested by a real-world surprise. Each option has a
 * concrete consequence captured by `commitRepair` in the hook.
 */
export default function RepairEventScreen({ repair, vehicleName, emergencyFund, onChoose }: Props) {
  if (!repair.amount) return null;
  const cushionShort = repair.amount > emergencyFund;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 mx-auto rounded-2xl border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-amber-500" />
        </div>
        <h2 className="font-heading text-lg font-bold text-foreground">Surprise Repair</h2>
        <p className="text-sm text-muted-foreground">
          Four months into owning <span className="font-semibold text-foreground">{vehicleName}</span>, life intervenes.
        </p>
      </div>

      <div className="glass-card glow-border p-5 space-y-2">
        <p className="text-sm text-foreground">{repair.description}</p>
        <div className="flex items-center justify-between border-t border-border pt-2">
          <span className="text-xs text-muted-foreground">Estimated repair cost</span>
          <span className="font-heading font-bold text-foreground">{fmt(repair.amount)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Your emergency fund</span>
          <span className={`font-mono text-sm ${cushionShort ? "text-amber-500" : "text-emerald-500"}`}>
            {fmt(emergencyFund)}
          </span>
        </div>
        {cushionShort && (
          <p className="text-xs text-amber-500/90 italic pt-1">
            Your cushion does not fully cover this. The choice matters.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Button
          onClick={() => onChoose("pay_cash")}
          className="w-full justify-start gap-3 h-auto py-3"
          variant="outline"
        >
          <PiggyBank className="w-4 h-4 text-emerald-500 shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-sm">Pay from savings</p>
            <p className="text-[11px] text-muted-foreground">One hit. Cushion drops by {fmt(repair.amount)}.</p>
          </div>
        </Button>
        <Button
          onClick={() => onChoose("use_credit")}
          className="w-full justify-start gap-3 h-auto py-3"
          variant="outline"
        >
          <CreditCard className="w-4 h-4 text-amber-500 shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-sm">Put it on credit</p>
            <p className="text-[11px] text-muted-foreground">Keep the cash. Pay interest until cleared (~+18%).</p>
          </div>
        </Button>
        <Button
          onClick={() => onChoose("postpone")}
          className="w-full justify-start gap-3 h-auto py-3"
          variant="outline"
        >
          <Clock className="w-4 h-4 text-destructive shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-sm">Postpone the repair</p>
            <p className="text-[11px] text-muted-foreground">Risk grows. Most postponed repairs cost ~40% more later.</p>
          </div>
        </Button>
      </div>

      <p className="text-[11px] text-center text-muted-foreground italic">
        No redos — your choice locks in for this run.
      </p>
    </div>
  );
}